import { PartOption } from '../hooks/useGetPartOptions';
import { PartType } from '../hooks/useGetSettings';

export type PartOptionTypeFilter = 'OEM' | 'OE Surplus' | 'A/M' | 'LKQ' | 'Re-Con' | 'Re-Man';

export type ValidityFilter = 'all' | 'valid' | 'invalid';

export type DeliveryTimePreset = 'today' | 'next-day' | 'two-days' | 'range';

export interface DeliveryDaysRange {
    min?: number;
    max?: number;
}

export interface PartOptionFilters {
    partTypes?: PartOptionTypeFilter[];
    validity?: ValidityFilter;
    deliveryTime?: DeliveryTimePreset;
    deliveryDaysRange?: DeliveryDaysRange;
}

export const PART_OPTION_TYPE_FILTERS: PartOptionTypeFilter[] = [
    'OEM',
    'OE Surplus',
    'A/M',
    'LKQ',
    'Re-Con',
    'Re-Man',
];

export function getPartOptionTypeCategory(type: string): PartOptionTypeFilter | 'Other' {
    const normalized = (type ?? '').trim().toLowerCase();
    if (!normalized) return 'Other';

    switch (normalized as PartType | 'other') {
        case 'oem':
        case 'oe-surplus':
        case 'a/m':
        case 'rcy':
        case 're-con':
        case 're-man':
            return getPartOptionTypeCategoryFromEnum(normalized as PartType);
    }

    const upper = type.toUpperCase();

    if (upper.includes('OE SURPLUS') || upper.includes('OE-SURPLUS')) {
        return 'OE Surplus';
    }
    if (upper.includes('OEM') || (upper.includes('OE') && !upper.includes('SURPLUS'))) {
        return 'OEM';
    }
    if (
        upper.includes('A/M')
        || upper.includes('AFTERMARKET')
        || upper.includes('CAPA')
        || /\bAM\b/.test(upper)
    ) {
        return 'A/M';
    }
    if (
        upper.includes('LKQ')
        || upper.includes('RECYCLED')
        || upper.includes('RCY')
        || upper.includes('USED')
    ) {
        return 'LKQ';
    }
    if (upper.includes('RE-MAN') || upper.includes('REMAN')) {
        return 'Re-Man';
    }
    if (upper.includes('RE-CON') || upper.includes('RECON')) {
        return 'Re-Con';
    }

    return 'Other';
}

function getPartOptionTypeCategoryFromEnum(type: PartType): PartOptionTypeFilter {
    switch (type) {
        case 'oem': return 'OEM';
        case 'oe-surplus': return 'OE Surplus';
        case 'a/m': return 'A/M';
        case 'rcy': return 'LKQ';
        case 're-con': return 'Re-Con';
        case 're-man': return 'Re-Man';
    }
}

export function isPartOptionValid(option: PartOption): boolean {
    return option.validity?.isValid !== false;
}

export function getPartOptionInvalidReason(option: PartOption): string | null {
    if (option.validity?.isValid === false) {
        return option.validity.reason;
    }
    return null;
}

export function optionMatchesDeliveryFilter(option: PartOption, filters: PartOptionFilters): boolean {
    if (!filters.deliveryTime) return true;

    const hours = option.delivery;
    if (hours == null || hours <= 0) return false;

    switch (filters.deliveryTime) {
        case 'today':
            return hours <= 24;
        case 'next-day':
            return hours < 48;
        case 'two-days':
            return hours < 72;
        case 'range': {
            const range = filters.deliveryDaysRange;
            if (range?.min == null || range?.max == null) return true;
            const days = hours / 24;
            return days >= range.min && days <= range.max;
        }
        default:
            return true;
    }
}

export function isDeliveryFilterActive(filters: PartOptionFilters): boolean {
    if (!filters.deliveryTime) return false;
    if (filters.deliveryTime === 'range') {
        const range = filters.deliveryDaysRange;
        return range?.min != null && range?.max != null;
    }
    return true;
}
export function applyPartOptionFilters(
    options: PartOption[],
    filters: PartOptionFilters,
): PartOption[] {
    const hasPartTypes = !!(filters.partTypes && filters.partTypes.length > 0);
    const validityFilter = filters.validity ?? 'all';
    const hasDelivery = isDeliveryFilterActive(filters);

    if (!hasPartTypes && validityFilter === 'all' && !hasDelivery) {
        return options;
    }

    return options.filter((option) => {
        if (hasPartTypes) {
            const category = getPartOptionTypeCategory(option.type);
            if (!filters.partTypes!.includes(category as PartOptionTypeFilter)) {
                return false;
            }
        }

        if (validityFilter === 'valid' && !isPartOptionValid(option)) {
            return false;
        }
        if (validityFilter === 'invalid' && isPartOptionValid(option)) {
            return false;
        }

        if (hasDelivery && !optionMatchesDeliveryFilter(option, filters)) {
            return false;
        }

        return true;
    });
}

export function getAvailablePartTypesFromOptions(options: PartOption[]): PartOptionTypeFilter[] {
    const categories = new Set<PartOptionTypeFilter>();
    for (const option of options) {
        const cat = getPartOptionTypeCategory(option.type);
        if (cat !== 'Other') {
            categories.add(cat);
        }
    }
    return PART_OPTION_TYPE_FILTERS.filter((label) => categories.has(label));
}
